import { useEffect, useState } from 'react';
import { resolveFirebaseImageUrl } from '../../Helpers/ImageLoader';
import type { PdfDocument, PdfPage } from '../../utils/pdf/pdfjs';

/**
 * Loads a drawing's single-page PDF source (plan §5.1) once per storage path and
 * hands back page 1. pdf.js is a lazy chunk, fetched on the first call.
 *
 * URL resolution defaults to the same daily download-URL cache the raster uses
 * (`resolveFirebaseImageUrl`). The guest page has no Storage SDK access and
 * passes its signed-URL callable as `resolveUrl` instead; the parsed document is
 * cached by storage path, so a signed URL's expiry does not matter after load.
 */
export interface UsePdfDocumentOptions {
    /** Storage path → fetchable URL. Defaults to the Firebase download-URL cache. */
    resolveUrl?: (storagePath: string) => Promise<string>;
}

export interface UsePdfDocumentResult {
    page: PdfPage | null;
    loading: boolean;
    error: Error | null;
}

interface CacheEntry {
    promise: Promise<PdfPage>;
    document: PdfDocument | null;
    /** Mounted hooks holding this document. Only entries nobody holds are evicted. */
    holders: number;
}

/**
 * Parsed documents, keyed by storage path. Small LRU over the documents no
 * mounted viewer holds: a list page with many drawings may hold more than the
 * limit at once, and destroying a document under a live canvas would freeze it.
 */
const MAX_CACHED_DOCUMENTS = 8;
const cache = new Map<string, CacheEntry>();
const warned = new Set<string>();

function destroyDocument(document: PdfDocument | null): void {
    if (!document) return;
    // Best effort: a failing or already destroyed worker must not throw here.
    Promise.resolve()
        .then(() => document.destroy())
        .catch(() => undefined);
}

function destroyEntry(entry: CacheEntry): void {
    destroyDocument(entry.document);
    entry.document = null;
}

function touch(storagePath: string, entry: CacheEntry): void {
    cache.delete(storagePath);
    cache.set(storagePath, entry);
    if (cache.size <= MAX_CACHED_DOCUMENTS) return;
    for (const [key, candidate] of cache) {
        if (cache.size <= MAX_CACHED_DOCUMENTS) break;
        if (key === storagePath || candidate.holders > 0) continue;
        cache.delete(key);
        destroyEntry(candidate);
    }
}

async function loadFirstPage(storagePath: string, resolveUrl: (path: string) => Promise<string>, entry: CacheEntry): Promise<PdfPage> {
    const [{ loadPdfJs }, url] = await Promise.all([
        import('../../utils/pdf/pdfjs'),
        resolveUrl(storagePath),
    ]);
    const pdfjs = await loadPdfJs();
    const document = await pdfjs.getDocument({ url }).promise;
    entry.document = document;
    try {
        return await document.getPage(1);
    } catch (err) {
        entry.document = null;
        destroyDocument(document);
        throw err;
    }
}

function lookup(storagePath: string, resolveUrl: (path: string) => Promise<string>): CacheEntry {
    const existing = cache.get(storagePath);
    if (existing) {
        touch(storagePath, existing);
        return existing;
    }
    const entry: CacheEntry = { promise: Promise.resolve() as unknown as Promise<PdfPage>, document: null, holders: 0 };
    entry.promise = loadFirstPage(storagePath, resolveUrl, entry).catch((err: unknown) => {
        // A failed load must not poison the cache; the next mount retries.
        if (cache.get(storagePath) === entry) cache.delete(storagePath);
        throw err;
    });
    touch(storagePath, entry);
    return entry;
}

/**
 * Imperative form: resolve page 1 of a drawing's source, cached by storage path.
 * Holds no reference; the page may be destroyed by a later eviction.
 */
export function getPdfPage(storagePath: string, resolveUrl: (path: string) => Promise<string> = resolveFirebaseImageUrl): Promise<PdfPage> {
    return lookup(storagePath, resolveUrl).promise;
}

/**
 * Like getPdfPage, but keeps the document out of eviction until `release` is
 * called. The hook acquires on mount and releases on unmount.
 */
export function acquirePdfPage(
    storagePath: string,
    resolveUrl: (path: string) => Promise<string> = resolveFirebaseImageUrl
): { promise: Promise<PdfPage>; release: () => void } {
    const entry = lookup(storagePath, resolveUrl);
    entry.holders += 1;
    let released = false;
    return {
        promise: entry.promise,
        release: () => {
            if (released) return;
            released = true;
            entry.holders = Math.max(0, entry.holders - 1);
        },
    };
}

/** Test hook: forget every cached document. */
export function clearPdfDocumentCache(): void {
    cache.forEach(destroyEntry);
    cache.clear();
    warned.clear();
}

/** Test hook: number of cached documents. */
export function pdfDocumentCacheSize(): number {
    return cache.size;
}

export function usePdfDocument(
    storagePath: string | null | undefined,
    options: UsePdfDocumentOptions = {}
): UsePdfDocumentResult {
    const { resolveUrl } = options;
    const [state, setState] = useState<UsePdfDocumentResult & { forPath: string | null }>({
        page: null,
        loading: !!storagePath,
        error: null,
        forPath: storagePath ?? null,
    });

    useEffect(() => {
        if (!storagePath) {
            setState({ page: null, loading: false, error: null, forPath: null });
            return;
        }
        let cancelled = false;
        setState({ page: null, loading: true, error: null, forPath: storagePath });
        const held = acquirePdfPage(storagePath, resolveUrl ?? resolveFirebaseImageUrl);
        held.promise
            .then(page => {
                if (!cancelled) setState({ page, loading: false, error: null, forPath: storagePath });
            })
            .catch((err: unknown) => {
                // A drawing that has a source but cannot render is a data error (D19):
                // say so once, then leave the raster on screen.
                if (!warned.has(storagePath)) {
                    warned.add(storagePath);
                    console.warn(`[usePdfDocument] Kunde inte läsa ritningens PDF-källa ${storagePath}`, err);
                }
                if (!cancelled) {
                    setState({
                        page: null,
                        loading: false,
                        error: err instanceof Error ? err : new Error(String(err)),
                        forPath: storagePath,
                    });
                }
            });
        return () => {
            cancelled = true;
            held.release();
        };
    }, [storagePath, resolveUrl]);

    // Never hand a page that belongs to a previous path to the caller.
    if (state.forPath !== (storagePath ?? null)) {
        return { page: null, loading: !!storagePath, error: null };
    }
    return { page: state.page, loading: state.loading, error: state.error };
}
